import type { FragmentType } from "@repo/graphql-types/fragment-masking";
import { useFragment } from "@repo/graphql-types/fragment-masking";
import { graphql } from "@repo/graphql-types/gql";
import { useSearch, useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PatientAvatar } from "@/components/patient-avatar";

export const PatientDetailHeaderFragment = graphql(/* GraphQL */ `
  fragment PatientDetailHeaderFragment on pacientes {
    telefone
    nome
    email
    codPaciente
    ...PatientAvatarFragment
  }
`);

interface PatientDetailHeaderProps {
  data: FragmentType<typeof PatientDetailHeaderFragment>;
  children?: ReactNode;
}

export const PatientDetailHeader = ({
  data,
  children,
}: PatientDetailHeaderProps): JSX.Element => {
  const patient = useFragment(PatientDetailHeaderFragment, data);

  const { nome, codPaciente } = patient;
  const router = useRouter();
  const searchParams = useSearch({
    from: "/patients/$patientId/",
  });

  const isModalOverlayOpen: boolean = searchParams.action === "expand-avatar-image";

  const openModalOverlay = (): void => {
    void router.navigate({
      to: "/patients/$patientId/",
      params: { patientId: String(codPaciente) },
      search: { ...searchParams, action: "expand-avatar-image" },
    });
  };

  const closeModalOverlay = (): void => {
    if (isModalOverlayOpen) {
      router.history.back();
    }
  };

  const handleOpenModalOverlay = (shouldOpen: boolean): void => {
    shouldOpen ? openModalOverlay() : closeModalOverlay();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between items-center">
        <div className="flex items-center">
          <PatientAvatar
            shouldOpenModalOverlay
            isModalOverlayOpen={isModalOverlayOpen}
            setIsModalOverlayOpen={() => handleOpenModalOverlay(!isModalOverlayOpen)}
            color="primaryLight"
            data={patient}
            size={72}
          />
        </div>

        {children}
      </div>
      <p className="font-medium text-xl text-neutral-600">{nome}</p>
    </div>
  );
};
